import { colors, intl, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { FC } from 'react'

const Full = styled.div`
	position: fixed;
	inset: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 16px;
	padding: 48px;
	text-align: center;
	background: ${colors.darkIndigo};
	color: ${colors.white};
`

const Eyebrow = styled.div`
	font-size: 18px;
	font-weight: 700;
	letter-spacing: 0.12em;
	text-transform: uppercase;
	color: ${colors.white70};
`

const Subject = styled.h1`
	margin: 0;
	font-size: 64px;
	line-height: 1.1;
	font-weight: 700;
	max-width: 1400px;
`

const Venue = styled.div`
	font-size: 26px;
	color: ${colors.white70};
`

/**
 * What the projector shows before the stage can: while the presentation
 * window waits for the console's first snapshot, and when the console is gone.
 * The audience sees it, so it says nothing about windows or buttons.
 */
export const ProjectorTitleCard: FC<{ subject?: string; venue?: string }> = ({
	subject,
	venue,
}) => (
	<Full data-testid="presenter.projector.title_card">
		<Eyebrow>
			<intl.Translate>in_person.presenter.projector.eyebrow</intl.Translate>
		</Eyebrow>
		{subject ? <Subject>{subject}</Subject> : <ui.Spinner size={64} />}
		{venue ? <Venue>{venue}</Venue> : null}
	</Full>
)

/** A message for whoever looks at the presentation window itself. */
export const ProjectorNotice: FC<{ messageKey: string }> = ({ messageKey }) => (
	<Full data-testid="presenter.projector.notice">
		<Venue>
			<intl.Translate>{messageKey}</intl.Translate>
		</Venue>
	</Full>
)

/** Black screen: covers everything, the stage keeps running underneath. */
export const ProjectorCover = styled.div`
	position: fixed;
	inset: 0;
	z-index: 10000;
	background: ${colors.black};
`

const Offer = styled.div`
	position: fixed;
	right: 32px;
	bottom: 32px;
	z-index: 10001;
	display: flex;
	align-items: center;
	gap: 16px;
	padding: 12px 12px 12px 20px;
	border-radius: 16px;
	background: ${colors.white};
	box-shadow: 0 6px 24px ${colors.black15};
	color: ${colors.darkIndigo};
	font-size: 15px;
`

/**
 * Shown while the presentation window is not full screen: one click here (or
 * any key) makes it so. Browsers that cannot take the console's delegated
 * click - Safari, Firefox - need it; everywhere else it is the way back after
 * Esc.
 */
export const FullscreenOffer: FC<{ onClick: () => void }> = ({ onClick }) => (
	<Offer data-testid="presenter.projector.fullscreen_offer">
		<intl.Translate>in_person.presenter.projector.press_any_key</intl.Translate>
		<ui.ButtonPrimary
			small
			onClick={onClick}
			data-testid="presenter.projector.fullscreen"
		>
			<intl.Translate>in_person.presenter.full_screen</intl.Translate>
		</ui.ButtonPrimary>
	</Offer>
)
